<template>
  <!-- {{ monthCalendar }} -->
  <div v-if="view == 'week'">
    <div
      v-if="showHeaders"
      class="grid grid-cols-7 gap-4 text-secondary text-sm text-center"
    >
      <div>Mon</div>
      <div>Tue</div>
      <div>Wed</div>
      <div>Thu</div>
      <div>Fri</div>
      <div>Sat</div>
      <div>Sun</div>
    </div>
    <div class="grid grid-cols-7 text-center gap-4 mt-2">
      <div
        v-for="day in modelValue"
        :key="day.period"
        class="aspect-square flex items-center justify-center"
      >
        <div
          v-if="day && day.score !== null && day.score !== undefined"
          class="pie"
          :class="{ 'pie-working': isWorkingDay(day) }"
          :style="`background: conic-gradient(var(--${getStatusColor(day)}) 0% ${day.score * 100}%, var(--bg-tertiary) ${day.score * 100 + 1}%)`"
        ></div>
        <div
          v-else
          class="pie"
        ></div>
      </div>
    </div>
  </div>

  <div v-if="view == 'month'">
    <div
      v-if="showHeaders"
      class="grid grid-cols-7 gap-4 text-secondary text-sm text-center"
    >
      <div>Mon</div>
      <div>Tue</div>
      <div>Wed</div>
      <div>Thu</div>
      <div>Fri</div>
      <div>Sat</div>
      <div>Sun</div>
    </div>
    <div class="grid grid-cols-7 text-center gap-4 mt-2">
      <div
        v-for="day in monthCalendar"
        :key="day.period"
        class="aspect-square flex items-center justify-center"
      >
        <div
          v-tippy="{
            content: `<div class='text-white text-sm mb-1'>${day.period}</div><div class='text-center text-lg font-bold'>${numberFormatter(day.score, 'number', 0)}%</div>`
          }"
          class="pie"
          :class="{ 'focus-ring-brand': isWorkingDay(day.dayData) }"
          :style="[
            day.score >= 0
              ? `background: conic-gradient(var(--${getStatusColor(day.dayData)}) 0% ${day.score}%, var(--bg-tertiary) ${day.score + 1}%)`
              : ''
          ]"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import dayjs from 'dayjs'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  view: {
    type: String,
    default: 'month' // week, month, year
  },
  showHeaders: {
    type: Boolean,
    default: true
  },
  monthDate: {
    type: String,
    default: ''
  }
})

/**
 * Counts how many KPIs are hit (>= 1.0) for a given day
 * @param {Object} dayData - The day data object containing KPI values
 * @returns {number} Number of KPIs that are hit (0-5)
 */
const countKpisHit = dayData => {
  if (!dayData) return 0

  const kpis = [
    dayData.productivity,
    dayData.answerRate,
    dayData.opportunityRate,
    dayData.conversionRate,
    dayData.appVsCallConversion
  ]

  return kpis.filter(kpi => kpi !== null && kpi !== undefined && kpi >= 1.0).length
}

/**
 * Determines the status color based on how many KPIs are hit
 * @param {Object} dayData - The day data object
 * @returns {string} The CSS variable name for the status color
 */
const getStatusColor = dayData => {
  const kpisHit = countKpisHit(dayData)

  if (kpisHit >= 5) {
    return 'bg-success-solid'
  } else if (kpisHit >= 3) {
    return 'bg-warning-solid'
  } else {
    return 'bg-error-solid'
  }
}

/**
 * Checks if the user is working on a given day
 * @param {Object} dayData - The day data object
 * @returns {boolean} True if the user is working that day (has productiveHours)
 */
const isWorkingDay = dayData => {
  if (!dayData) return false
  return dayData.productiveHours !== null && dayData.productiveHours !== undefined
}

const monthYear = computed(() => dayjs(props.monthDate || undefined).format('YYYY-MM'))
const monthCalendar = computed(() => {
  const days = []
  const startDay = dayjs(monthYear.value).startOf('month').day()
  const totalDays = dayjs(monthYear.value).daysInMonth()
  for (let i = 1; i < totalDays + startDay; i++) {
    const date = dayjs(dayjs(monthYear.value).format('YYYY-MM-') + (i - startDay + 1))
    if (i >= startDay) {
      const dayData = props.modelValue.find(item => item.period == date.format('YYYY-MM-DD'))
      days.push({
        period: date.format('YYYY-MM-DD'),
        isPadding: false,
        day: date.format('D'),
        score: dayData?.score * 100 || null,
        dayData: dayData
      })
    } else {
      days.push({
        period: null,
        isPadding: true,
        day: null,
        score: null,
        dayData: null
      })
    }
  }

  return days
})
</script>

<style>
.pie {
  display: inline-block;
  height: 100%;
  width: 100%;
  max-width: 35px;
  max-height: 35px;
  border-radius: 50%;
  background: conic-gradient(var(--bg-quaternary) 0% 100%);
  border: 1px solid var(--border-primary);
}
</style>
